// client/src/coms/components/DndSetting.js
// Do Not Disturb: suppresses push notifications for messages and DMs on every device, and shows a red status to
// others. Any duration (minutes, hours, days, weeks) or until turned off; the same control and storage as the Status
// setting in Privacy & Security (presence/PresenceStatusSetting.js).
import React, { useEffect, useState } from 'react';
import { comsApiGet, comsApiPut } from '../comsApi';
import { Row } from '../personalisation/settingsControls';
import { DurationPicker } from '../presence/PresenceUi';
import { DEFAULT_DURATION, durationError, formatEnds, formatRemaining, toApiDuration } from '../presence/durations';
import { PRESENCE_EVENT, setMyStatus } from '../presence/presenceClient';

export default function DndSetting() {
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [, tick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () =>
      comsApiGet('/me/notification-settings')
        .then((s) => alive && setState(s))
        .catch(() => {
          if (!alive) return;
          setState({ dnd: false, dnd_until_ms: 0 });
          setErr('Could not load your Do Not Disturb setting.');
        });
    load();
    window.addEventListener(PRESENCE_EVENT, load);
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => {
      alive = false;
      window.removeEventListener(PRESENCE_EVENT, load);
      clearInterval(t);
    };
  }, []);

  async function turnOn() {
    setErr('');
    setBusy(true);
    try {
      await setMyStatus('dnd', toApiDuration(duration));
    } catch (e) {
      setErr(String((e && e.message) || 'Could not change Do Not Disturb.'));
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    setErr('');
    setBusy(true);
    try {
      setState(await comsApiPut('/me/notification-settings', { dnd_minutes: 0 }));
      try {
        window.dispatchEvent(new CustomEvent(PRESENCE_EVENT, { detail: null }));
      } catch {}
    } catch (e) {
      setErr(String((e && e.data && e.data.error) || 'Could not change Do Not Disturb.'));
    } finally {
      setBusy(false);
    }
  }

  const until = state && state.dnd ? Number(state.dnd_until_ms) : 0;
  const active = until > Date.now();
  const status = !state ? 'Loading…' : active ? (until > 8e15 ? 'On until you turn it off.' : `On for ${formatRemaining(until - Date.now())} more (until ${formatEnds(until)}).`) : 'Off: you get notifications as usual.';
  const dErr = durationError(duration);
  return (
    <Row label="Do Not Disturb" hint={`No message or DM notifications on any of your devices, and a red status for others. ${status}`} settingId="dnd" scope="account">
      <div className="vx-inline" data-testid="dnd-setting" style={{ flexWrap: 'wrap', gap: 8 }}>
        {active ? (
          <button type="button" className="vx-btn small primary" disabled={busy} onClick={turnOff} data-testid="dnd-off">
            Turn off
          </button>
        ) : (
          <>
            <DurationPicker value={duration} onChange={setDuration} disabled={busy || !state} testId="dnd-duration" />
            <button type="button" className="vx-btn small primary" disabled={busy || !state || !!dErr} onClick={turnOn} data-testid="dnd-on">
              Turn on
            </button>
          </>
        )}
      </div>
      {err ? (
        <div className="vx-acct-error" role="alert">
          {err}
        </div>
      ) : null}
    </Row>
  );
}
